import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import AuthService from '../services/AuthService'
import store from '@/store'
import router from '@/router'
import main from '@/main'
import { ERR_API_KEY } from '@/constants';

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const axiosInstance = axios.create();

// Request interceptor for API calls
axiosInstance.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    let token = store.state.auth.token

    if (token) {
      config.headers = {
        ...config.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json;charset=UTF-8',
        'Accept': 'application/json',
        'Access-Control-Allow-Origin': '*',
      }
    }
    return config;
  },
  error => {
    Promise.reject(error)
  }
)

// Response interceptor for API calls
axiosInstance.interceptors.response.use((response: AxiosResponse) => {
  return response
}, async (error) => {
    const originalRequest = error ? error.config : null;

    if (!error.response || !originalRequest) {
      return Promise.reject(error);
    }

    const { status, data } = error.response;

    if (status === 401 && !originalRequest._retry && data?.status_code === ERR_API_KEY.TOKEN_EXPIRED) {
      
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return axiosInstance(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise((resolve, reject) => {
        AuthService.refreshAccessToken()
          .then(access_token => {
            axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + access_token;
            originalRequest.headers['Authorization'] = 'Bearer ' + access_token;
            
            processQueue(null, access_token);
            resolve(axiosInstance(originalRequest));
          })
          .catch((err) => {
            processQueue(err, null);
            handleLogout();
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    if (
      status === 401 &&
      [
        ERR_API_KEY.INVALID_USER,
        ERR_API_KEY.INVALID_TOKEN,
        ERR_API_KEY.REFRESH_TOKEN_EXPIRED,
        ERR_API_KEY.REFRESH_TOKEN_INVALID
      ].includes(data?.status_code)
    ) {
      handleLogout();
    }

    if (
      status === 401 &&
      data?.status_code === ERR_API_KEY.USER_NOT_BE_LONG_TO_GROUP &&
      !['login', 'line-login', 'withdraw'].includes(String(main.$route.name))
    ) {
      if (main.$route.name !== 'employee-without-group') {
        router.push({ name: 'employee-without-group' });
      }
    }

    if (status === 403 && data?.message === "This action is unauthorized.") {
      setTimeout(() => router.push({ name: 'home' }), 3000);
    }
    
    if (status === 404 && [ERR_API_KEY.GROUP_NOT_FOUND, ERR_API_KEY.GROUP_IS_STOPPED].includes(data?.message)) {
      main.$bvModal.show('modal-error-group-not-found-or-deleted');
    }

    if (status === 400 && data?.message === ERR_API_KEY.EXCEED_COMPANY_STORAGE_CAPACITY) {
      main.$bvModal.show('modal-error-company-storage-exceed');
    }

    if (status === 429) {
      main.$bvModal.show('modal-api-exceed');
    }

    return Promise.reject(error);
  }
);

function handleLogout() {
  sessionStorage.clear();
  localStorage.clear();
  if (router.currentRoute.name !== 'login') {
    router.push({ name: 'login' });
  }
}

export default axiosInstance;
